// Privacy Policy content as structured data so it can be rendered both in the
// pre-auth login modal (PrivacyPolicyModal) and in Settings -> Privacy
// (PrivacySection) without duplicating the text.
//
// NOTE: This is an engineering draft of a HIPAA Business-Associate / SOC 2-aligned
// privacy policy for an EMR-integrated clinical tool. It is informational and is
// NOT a substitute for review by legal counsel before being relied upon publicly.

export const PRIVACY_POLICY_EFFECTIVE_DATE = 'June 1, 2026';
export const PRIVACY_POLICY_CONTACT_EMAIL = 'admin@percix.ai';

export interface PrivacyPolicySection {
  heading: string;
  /** Each entry is one paragraph. */
  paragraphs?: string[];
  /** Optional bulleted list rendered after the paragraphs. */
  bullets?: string[];
}

export const PRIVACY_POLICY_INTRO: string[] = [
  'Percix ("Percix", "we", "us", or "our") provides PT-Copilot, a clinician-facing' +
    ' assistant that integrates with your electronic health record ("EHR") system' +
    ' to surface patient context and assist with physical-therapy documentation.' +
    ' PT-Copilot consists of a web portal and a companion browser extension that' +
    ' the portal uses to exchange information with your EHR (the "Service").',
  'This Privacy Policy explains what information the Service handles, how and why' +
    ' we handle it, and the safeguards we apply. It is written for the clinicians' +
    ' and provider organizations who use PT-Copilot, and it describes our' +
    ' commitments regarding protected health information ("PHI").',
];

export const PRIVACY_POLICY_SECTIONS: PrivacyPolicySection[] = [
  {
    heading: '1. Our role under HIPAA',
    paragraphs: [
      'When we handle PHI on behalf of a covered entity (a provider organization or' +
        ' clinic), Percix acts as a Business Associate as defined by the Health' +
        ' Insurance Portability and Accountability Act and its implementing' +
        ' regulations ("HIPAA"). Our handling of PHI is governed by a Business' +
        ' Associate Agreement ("BAA") executed with the covered entity.',
      'We use and disclose PHI only as permitted by that BAA and by HIPAA — to' +
        ' provide and support the Service, as required by law, or as otherwise' +
        ' authorized by the covered entity or the individual. We apply the' +
        ' minimum-necessary principle to all PHI we access or process.',
      'We do not sell PHI. We do not use PHI for advertising, marketing, or to' +
        ' train models for use outside the providing organization, except as' +
        ' expressly permitted by the applicable BAA.',
    ],
  },
  {
    heading: '2. What the extension accesses',
    paragraphs: [
      'The PT-Copilot browser extension is a thin client that acts only when you' +
        ' ask it to. It has access to the supported EHR sites it is authorized for' +
        ' (today, WebPT) and to the PT-Copilot portal — and to no other website.' +
        ' It runs only in response to an action you start in the portal, such as' +
        ' importing patient information or submitting a note; it does not run in' +
        ' the background or observe your browsing.',
      'READING. When you ask it to import information, the extension opens the' +
        ' relevant EHR page and reads the specific fields that action needs — for' +
        ' example patient demographics, the case list, or the list of open Daily' +
        ' Notes — either from the page or from the EHR’s own interfaces, using' +
        ' the session you are already signed in to.',
      'WRITING. When you choose to submit a note, the extension creates a new' +
        ' Daily Note in your EHR, or appends to an existing one, with the content' +
        ' prepared in the portal. It fills the note in and stops there: the' +
        ' extension never signs, finalizes, or submits the note for you. Reviewing' +
        ' and signing remains entirely yours, and the EHR tab is left open so you' +
        ' can check the result.',
      'The extension does not store clinical information. It keeps no extension' +
        ' storage, writes nothing to disk, and retains no patient data after an' +
        ' action finishes. It sends nothing to any third party: clinical content' +
        ' travels only between your browser and your EHR, over encrypted' +
        ' connections. It reads your EHR session credentials only to authenticate' +
        ' that request, and never stores, logs, or transmits them anywhere else.',
      'To help us support you when a submission fails, the extension returns a' +
        ' technical record of the action to the portal — which step ran, whether it' +
        ' succeeded, and how long it took. This diagnostic record deliberately' +
        ' excludes the contents of any field, and so contains no clinical' +
        ' information.',
    ],
    bullets: [
      'Runs only on the PT-Copilot portal and authorized EHR sites (today, WebPT).',
      'Acts only when you start an action; never runs in the background.',
      'Reads only the fields the requested action needs.',
      'Creates or appends a Daily Note when you submit one — and never signs it.',
      'Stores no clinical data, and keeps no browser extension storage.',
      'Sends clinical content only to your EHR — never to a third party.',
      'Returns a step-level diagnostic record that excludes all field contents.',
    ],
  },
  {
    heading: '3. Information we process',
    paragraphs: [
      'Depending on how your organization uses the Service, we may process the' +
        ' following categories of information:',
    ],
    bullets: [
      'Account and authentication data — such as your name, work email, role,' +
        ' and organization, used to sign you in and control access.',
      'Clinical data — patient and treatment information you enter, import from' +
        ' the EHR, or that the Service helps you document. This may include PHI.',
      'Usage and technical data — such as log entries, timestamps, device and' +
        ' browser metadata, and error diagnostics, used to operate, secure, and' +
        ' improve the Service.',
    ],
  },
  {
    heading: '4. How we use information',
    paragraphs: [
      'We use the information above to deliver the Service, authenticate users,' +
        ' generate and assist with clinical documentation, maintain security and' +
        ' audit trails, provide support, meet legal obligations, and improve' +
        ' reliability and performance. We limit each use to what is necessary for' +
        ' that purpose.',
    ],
  },
  {
    heading: '5. Security program (SOC 2)',
    paragraphs: [
      'We maintain an information-security program aligned to the SOC 2 Trust' +
        ' Services Criteria for security, availability, and confidentiality. Our' +
        ' controls are designed to protect information against unauthorized access,' +
        ' alteration, disclosure, or destruction.',
    ],
    bullets: [
      'Encryption of data in transit (TLS) and at rest.',
      'Role-based access controls and least-privilege access to systems and data.',
      'Audit logging and monitoring of access to sensitive resources.',
      'Network segmentation and hardened, regularly patched infrastructure.',
      'Employee security training and confidentiality obligations.',
      'Periodic risk assessments and independent security review.',
    ],
  },
  {
    heading: '6. Sub-processors and sharing',
    paragraphs: [
      'We host the Service on reputable cloud infrastructure (Amazon Web Services)' +
        ' and may engage a limited set of vetted sub-processors to operate it. Where' +
        ' those sub-processors may handle PHI, we require contractual commitments' +
        ' consistent with HIPAA, including Business Associate Agreements where' +
        ' applicable.',
      'We disclose information only to provide the Service, when required by law or' +
        ' legal process, to protect rights and safety, or with the authorization of' +
        ' the covered entity or individual. We do not sell personal information or' +
        ' PHI.',
    ],
  },
  {
    heading: '7. Data retention and deletion',
    paragraphs: [
      'We retain information for as long as needed to provide the Service and to' +
        ' meet the legal, contractual, and recordkeeping obligations of the' +
        ' providing organization. Retention of PHI is governed by the applicable' +
        ' BAA. On termination, or on a valid request from the covered entity, we' +
        ' return or securely destroy PHI as required by that agreement and by law.',
    ],
  },
  {
    heading: '8. Your rights and patient rights',
    paragraphs: [
      'The rights of individuals with respect to their PHI — including access,' +
        ' amendment, and an accounting of disclosures — are generally exercised' +
        ' through the covered entity that maintains the medical record. We support' +
        ' covered entities in fulfilling these requests as set out in the BAA. If' +
        ' you are a patient, please contact your provider to exercise these rights.',
    ],
  },
  {
    heading: '9. Breach notification',
    paragraphs: [
      'If we discover a breach of unsecured PHI, we will notify the affected' +
        ' covered entity without unreasonable delay and within the timeframes' +
        ' required by HIPAA, and we will cooperate in any required notifications and' +
        ' remediation.',
    ],
  },
  {
    heading: '10. Changes to this policy',
    paragraphs: [
      'We may update this Privacy Policy from time to time. When we make material' +
        ' changes, we will update the effective date above and, where appropriate,' +
        ' provide additional notice. Continued use of the Service after an update' +
        ' constitutes acceptance of the revised policy.',
    ],
  },
  {
    heading: '11. Contact us',
    paragraphs: [
      'If you have questions about this Privacy Policy or our privacy practices,' +
        ` contact us at ${PRIVACY_POLICY_CONTACT_EMAIL}.`,
    ],
  },
];
